import { useState, useEffect } from "react";
import { X, Send, CheckCircle, Loader2, MessageSquareHeart } from "lucide-react";
import StarRating from "./StarRating";
import useFeedback from "../hooks/useFeedback";
import { PROJECT_NAME } from "../../../core/config/Constant";

const FeedbackPanel = ({ isOpen, onClose }) => {
  const {
    existingFeedback,
    hasExistingFeedback,
    isFetching,
    handleSubmitFeedback,
    isSubmitting,
    isSuccess,
  } = useFeedback();

  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Pre-fill form when existing feedback loads
  useEffect(() => {
    if (hasExistingFeedback && existingFeedback) {
      setRating(parseFloat(existingFeedback.rating) || 0);
      setMessage(existingFeedback.feedback_message || "");
    }
  }, [hasExistingFeedback, existingFeedback]);

  // Auto-close after success
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        onClose();
        // Reset success state for next open
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, onClose]);

  // Reset form when panel closes
  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      if (!hasExistingFeedback) {
        setRating(0);
        setMessage("");
      }
    }
  }, [isOpen, hasExistingFeedback]);

  const validate = () => {
    const newErrors = {};
    if (!rating || rating <= 0) newErrors.rating = "Please select a rating";
    if (!message.trim()) newErrors.message = "Please share your feedback";
    else if (message.trim().length < 10) newErrors.message = "Feedback must be at least 10 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    handleSubmitFeedback({
      rating: parseFloat(rating),
      feedback_message: message.trim(),
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-[60] transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[70] flex justify-center pointer-events-none`}
      >
        <div
          className={`w-full max-w-lg bg-white rounded-t-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.12)] pointer-events-auto
            transform transition-transform duration-300 ease-out
            ${isOpen ? "translate-y-0" : "translate-y-full"}`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-5 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-primary-50 flex items-center justify-center">
                <MessageSquareHeart size={18} className="text-primary-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  {hasExistingFeedback ? "Update Your Feedback" : "Share Your Feedback"}
                </h3>
                <p className="text-xs text-gray-400">
                  {hasExistingFeedback ? "Modify your previous response" : "Help us improve your experience"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
              aria-label="Close feedback"
            >
              <X size={18} className="text-gray-400" />
            </button>
          </div>

          {/* Divider */}
          <div className="mx-6 border-t border-gray-100" />

          {/* Content */}
          {isFetching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-primary-500" />
            </div>
          ) : isSuccess ? (
            /* ── Success State ── */
            <div className="flex flex-col items-center justify-center py-10 px-6">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-3">
                <CheckCircle size={28} className="text-green-500" />
              </div>
              <p className="text-base font-semibold text-gray-900">Thank you!</p>
              <p className="text-sm text-gray-500 mt-1">
                Your feedback has been {hasExistingFeedback ? "updated" : "submitted"} successfully.
              </p>
            </div>
          ) : (
            /* ── Form ── */
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
              {/* Star Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2.5">
                  How would you rate your experience?
                </label>
                <StarRating value={rating} onChange={setRating} size={32} />
                {errors.rating && (
                  <p className="text-xs text-red-500 mt-1.5">{errors.rating}</p>
                )}
              </div>

              {/* Feedback Message */}
              <div>
                <label
                  htmlFor="feedback-message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Tell us more
                </label>
                <textarea
                  id="feedback-message"
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What did you like? What can we improve?"
                  className="w-full px-4 py-3 text-sm text-gray-800 placeholder-gray-400
                    border border-gray-200 rounded-xl resize-none
                    focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                    transition-all duration-200"
                  maxLength={1000}
                />
                <div className="flex items-center justify-between mt-1.5">
                  {errors.message ? (
                    <p className="text-xs text-red-500">{errors.message}</p>
                  ) : (
                    <span />
                  )}
                  <span className="text-xs text-gray-400">
                    {message.length}/1000
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-5 py-3
                  bg-primary-500 hover:bg-primary-600 active:bg-primary-700
                  text-white text-sm font-semibold rounded-xl
                  transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
                  shadow-sm hover:shadow-md"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>{hasExistingFeedback ? "Updating..." : "Submitting..."}</span>
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    <span>{hasExistingFeedback ? "Update Feedback" : "Submit Feedback"}</span>
                  </>
                )}
              </button>

              {/* Footer note */}
              <p className="text-center text-xs text-gray-400 pb-1">
                Your feedback helps us build a better {PROJECT_NAME} for everyone 🧡
              </p>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default FeedbackPanel;
